import { useState, useCallback } from 'react';
import api from '../lib/api';

interface ShareUrlResponse {
  shareToken: string;
  shareUrl: string;
  shortUrl: string;
  shortId: string;
}

interface PublicReport {
  report: {
    type: 'standard' | 'custom';
    reportType?: string;
    reportId?: number;
    name: string;
    description: string;
    config?: any;
  };
  workspace: {
    name: string;
    organization: string;
  };
}

interface ReportData {
  rows: any[];
  totalRows: number;
  executionTime: number;
  config?: any;
}

export const useReportSharing = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get or create share URL for a standard report
   */
  const getStandardReportShareUrl = useCallback(async (
    reportType: string,
    workspaceId: string
  ): Promise<ShareUrlResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get<{ success: boolean; data: ShareUrlResponse }>(
        `/public/reports/share/${reportType}/${workspaceId}`
      );
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to get share URL';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get or create share URL for an org-level standard report
   */
  const getOrgReportShareUrl = useCallback(async (
    reportType: string,
    organizationId: string
  ): Promise<ShareUrlResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<{ success: boolean; data: ShareUrlResponse }>(
        `/public/reports/share/org/${reportType}/${organizationId}`
      );
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to get share URL';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get or create share URL for a custom report
   */
  const getCustomReportShareUrl = useCallback(async (
    reportDefinitionId: number,
    workspaceId?: string
  ): Promise<ShareUrlResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const url = workspaceId 
        ? `/public/reports/share/custom/${reportDefinitionId}?workspaceId=${workspaceId}`
        : `/public/reports/share/custom/${reportDefinitionId}`;
      
      const response = await api.get<{ success: boolean; data: ShareUrlResponse }>(url);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to get share URL';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch public report metadata (no auth required)
   */
  const fetchPublicReport = useCallback(async (
    shareToken: string
  ): Promise<PublicReport> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get<{ success: boolean; data: PublicReport }>(
        `/public/reports/${shareToken}`
      );
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to load report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Execute public report (no auth required)
   */
  const executePublicReport = useCallback(async (
    shareToken: string,
    filters?: any
  ): Promise<ReportData> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.post<{ success: boolean; data: ReportData }>(
        `/public/reports/${shareToken}/execute`,
        { filters }
      );
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to execute report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Download public report
   */
  const downloadPublicReport = useCallback(async (
    shareToken: string,
    reportName: string,
    viewMode: 'table' | 'card' = 'table',
    format: 'csv' | 'excel' | 'pdf' | 'word' = 'csv'
  ): Promise<void> => {
    setIsDownloading(true);
    setError(null);
    
    try {
      const response = await api.get(
        `/public/reports/${shareToken}/download/${format}?viewMode=${viewMode}`,
        { responseType: 'blob' }
      );
      
      // Determine file extension
      const extension = format === 'word' ? 'docx' : format === 'excel' ? 'xlsx' : format;
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportName}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to download report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return {
    isLoading,
    isDownloading,
    error,
    getStandardReportShareUrl,
    getOrgReportShareUrl,
    getCustomReportShareUrl,
    fetchPublicReport,
    executePublicReport,
    downloadPublicReport
  };
};

export default useReportSharing;

